import React, { Component } from "react";
import { NumberPumpkin } from "./Art";
import { Prize, prizeLabel } from "./api";

// The wheel of 42 pumpkins. The server decides where it stops; this only
// draws the wheel and turns it until that pumpkin is under the pointer at the
// top. Every pumpkin is the same size because every pumpkin is equally
// likely, so the wheel never looks fairer than it is.

const SIZE = 300;
const CENTRE = SIZE / 2;
const RADIUS = 140;
const SPIN_MS = 5200;
const TURNS = 6;

// The pointer is a flapper: the pegs round the rim push it aside as they
// pass and it springs back, ticking fast at first and slowly at the end. It
// bends by up to this many degrees, starts to be pushed when a peg is this
// share of a pumpkin away, and springs back this stiffly.
const FLAP_BEND = 18;
const FLAP_REACH = 0.4;
const FLAP_STIFFNESS = 900;
const FLAP_DAMPING = 18;

// How far the wheel has turned, read from the spin the browser is animating.
const turnedBy = (element: HTMLElement) => {
  const match = /matrix\(\s*([-\d.e]+),\s*([-\d.e]+)/.exec(window.getComputedStyle(element).transform);
  return match ? (Math.atan2(Number(match[2]), Number(match[1])) * 180) / Math.PI : 0;
};

export const PRIZE_COLOURS: Record<Prize, { fill: string; alt: string; glyph: string }> = {
  pct20: { fill: "#ff7a1a", alt: "#f26a0a", glyph: "#2e2437" },
  pct13: { fill: "#b798f2", alt: "#a585e6", glyph: "#2e2437" },
  guide10: { fill: "#f5f0e1", alt: "#e8dcc4", glyph: "#2e2437" },
  pct42: { fill: "#f5b041", alt: "#f5b041", glyph: "#2e2437" },
};

const point = (angle: number, radius: number) => {
  const radians = (angle * Math.PI) / 180;
  return [CENTRE + radius * Math.sin(radians), CENTRE - radius * Math.cos(radians)];
};

const wedge = (start: number, end: number) => {
  const [x1, y1] = point(start, RADIUS);
  const [x2, y2] = point(end, RADIUS);
  return `M${CENTRE} ${CENTRE}L${x1.toFixed(2)} ${y1.toFixed(2)}A${RADIUS} ${RADIUS} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}Z`;
};

// A tiny pumpkin (or a tiny book, for the guide slices) on each slice.
const Glyph = ({ prize, x, y, angle }: { prize: Prize; x: number; y: number; angle: number }) => {
  const colour = PRIZE_COLOURS[prize].glyph;

  if (prize === "guide10") {
    return (
      <g transform={`translate(${x} ${y}) rotate(${angle})`}>
        <rect x="-4.2" y="-5" width="8.4" height="10" rx="1" fill="#233dff" stroke={colour} strokeWidth="1" />
        <path d="M-2 -2.6h4M-2 -.6h4" stroke="#f5f0e1" strokeWidth=".9" />
      </g>
    );
  }

  if (prize === "pct42") {
    return (
      <g transform={`translate(${x} ${y}) rotate(${angle})`}>
        <circle r="9" fill="#fff6c9" opacity=".55" className="hw-golden-glow" />
        <path d="M0 -5.6c0-1.8.9-3 2.3-3.4" stroke="#4f7a2c" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <ellipse cx="0" cy="0" rx="6.4" ry="5.4" fill="#ffd84a" stroke="#2e2437" strokeWidth="1.2" />
        <path d="M0 -5v10" stroke="#c4841d" strokeWidth=".9" />
        <path d="M8 -9l1 2.2 2.2 1-2.2 1-1 2.2-1-2.2-2.2-1 2.2-1z" fill="#fff" />
      </g>
    );
  }

  return (
    <g transform={`translate(${x} ${y}) rotate(${angle})`}>
      <path d="M0 -4.6c0-1.6.8-2.6 2-3" stroke="#4f7a2c" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      <ellipse cx="0" cy="0" rx="5.4" ry="4.6" fill={colour} />
      <path d="M0 -4.4v8.8" stroke={PRIZE_COLOURS[prize].fill} strokeWidth=".8" />
    </g>
  );
};

interface Props {
  wheel: Prize[];
  prizes: Record<Prize, number>;
  // Where to stop. Undefined until the server has answered.
  target?: number;
  onStopped: () => void;
}

interface State {
  rotation: number;
}

class Wheel extends Component<Props, State> {
  state: State = { rotation: 0 };
  private stopTimer?: number;
  private flapFrame?: number;
  private discRef = React.createRef<HTMLDivElement>();
  private flapRef = React.createRef<SVGGElement>();
  private reducedMotion =
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  componentDidUpdate(prevProps: Props) {
    if (this.props.target === undefined || prevProps.target === this.props.target) return;

    const slice = 360 / this.props.wheel.length;
    // Somewhere inside the pumpkin, not dead centre, the way a real wheel
    // comes to rest.
    const wobble = (Math.random() - 0.5) * slice * 0.5;
    const centre = (this.props.target + 0.5) * slice;
    this.setState({ rotation: TURNS * 360 - centre + wobble });

    window.clearTimeout(this.stopTimer);
    this.stopTimer = window.setTimeout(this.props.onStopped, this.reducedMotion ? 900 : SPIN_MS + 250);
    if (!this.reducedMotion) this.flap();
  }

  componentWillUnmount() {
    window.clearTimeout(this.stopTimer);
    window.cancelAnimationFrame(this.flapFrame);
  }

  // Bends the pointer frame by frame for as long as the wheel turns. The
  // wheel turns clockwise, so each peg comes from the left and pushes the
  // pointer's tip to the right until it slips past.
  flap = () => {
    const slice = 360 / this.props.wheel.length;
    const reach = slice * FLAP_REACH;
    const until = performance.now() + SPIN_MS + 800;
    let bend = 0;
    let speed = 0;
    let last = performance.now();

    const frame = (now: number) => {
      const disc = this.discRef.current;
      const flapper = this.flapRef.current;
      if (!disc || !flapper) return;

      const seconds = Math.min((now - last) / 1000, 1 / 30);
      last = now;

      const passed = ((turnedBy(disc) % slice) + slice) % slice;
      const toNextPeg = slice - passed;
      const pushedTo = toNextPeg < reach ? -FLAP_BEND * (1 - toNextPeg / reach) : 0;

      speed += (-FLAP_STIFFNESS * bend - FLAP_DAMPING * speed) * seconds;
      bend += speed * seconds;
      // A peg in the way holds the pointer at least this far over.
      if (pushedTo < 0 && bend > pushedTo) {
        bend = pushedTo;
        speed = 0;
      }

      flapper.setAttribute("transform", `rotate(${bend.toFixed(2)} 20 10)`);
      if (now < until || Math.abs(speed) > 0.5) this.flapFrame = window.requestAnimationFrame(frame);
    };

    window.cancelAnimationFrame(this.flapFrame);
    this.flapFrame = window.requestAnimationFrame(frame);
  };

  render() {
    const { wheel } = this.props;
    const slice = 360 / wheel.length;
    const legend: Prize[] = ["pct20", "guide10", "pct13", "pct42"];

    return (
      <div className="hw-wheel">
        <div className="hw-wheel__stage">
          <svg className="hw-wheel__pointer" viewBox="0 0 40 46" aria-hidden="true">
            <g ref={this.flapRef}>
              <path d="M20 44 4 8c6-6 26-6 32 0z" fill="#2e2437" stroke="#f5f0e1" strokeWidth="2.4" strokeLinejoin="round" />
              <circle cx="20" cy="12" r="4" fill="#f5b041" />
            </g>
          </svg>
          <div
            ref={this.discRef}
            className="hw-wheel__disc"
            style={{
              transform: `rotate(${this.state.rotation}deg)`,
              transitionDuration: `${this.reducedMotion ? 800 : SPIN_MS}ms`,
            }}
          >
            <svg viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label={`A wheel of ${wheel.length} pumpkins`}>
              <circle cx={CENTRE} cy={CENTRE} r={RADIUS + 8} fill="#2e2437" />
              {wheel.map((prize, index) => {
                const start = index * slice;
                const middle = start + slice / 2;
                const [gx, gy] = point(middle, RADIUS - 16);
                const colours = PRIZE_COLOURS[prize];
                return (
                  <g key={index}>
                    <path
                      d={wedge(start, start + slice)}
                      fill={index % 2 === 0 ? colours.fill : colours.alt}
                      stroke="#2e2437"
                      strokeWidth=".8"
                    />
                    <Glyph prize={prize} x={gx} y={gy} angle={middle} />
                  </g>
                );
              })}
              {wheel.map((_, index) => {
                const [bx, by] = point(index * slice, RADIUS + 4);
                return <circle key={`bulb-${index}`} className="hw-bulb" cx={bx} cy={by} r="1.8" fill="#f3ec7a" />;
              })}
              <circle cx={CENTRE} cy={CENTRE} r="42" fill="#2e2437" stroke="#f5b041" strokeWidth="2" />
            </svg>
          </div>
          {/* The 42 pumpkin sits on the hub but doesn't turn with the wheel,
              so it always ends the right way up. */}
          <svg className="hw-wheel__hub" viewBox="0 0 100 92" aria-hidden="true">
            <NumberPumpkin />
          </svg>
        </div>
        <ul className="hw-wheel__legend" aria-label="What is on the wheel">
          {legend.map((prize) => (
            <li key={prize}>
              <span className="swatch" style={{ background: PRIZE_COLOURS[prize].fill }} />
              {prizeLabel(prize, this.props.prizes)}
            </li>
          ))}
        </ul>
      </div>
    );
  }
}

export default Wheel;
